import { orderBy, sortBy } from "lodash-es";
import { AssemblyAIMessage, AudioCapture, TranscriptSnapshot } from "./types";
import { Table } from "dexie";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";

export class AudioCapturesDB {
  table: Table<AudioCapture>;

  constructor(table: Table<AudioCapture>) {
    this.table = table;
  }

  clear = async () => {
    return this.table.clear();
  };

  clearAudioCapturesByNoteId = async (noteId: string) => {
    return this.table.where({ noteId }).delete();
  };

  saveCapturedAudioId = async (noteId: string, capturedAudioId: string) => {
    return this.table.add({ noteId, capturedAudioId });
  };

  appendTranscript = async (noteId: string, transcript: AssemblyAIMessage) => {
    return this.table.add({ noteId, transcript });
  };

  appendTranscriptSnapshot = async (noteId: string, transcriptSnapshot: TranscriptSnapshot) => {
    // Use a transaction to prevent race conditions
    await this.table.db.transaction("rw", this.table, async () => {
      // Delete any existing transcripts before adding the snapshot
      await this.table
        .where({ noteId })
        .and(record => "transcript" in record)
        .delete();

      const transcripts = sortBy(
        Array.from(transcriptSnapshot.transcriptMap.values()),
        "audio_start"
      );

      await this.table.add({
        noteId,
        transcriptSnapshot: {
          transcripts,
          lastAudioEnd: transcriptSnapshot.lastAudioEnd,
          snapshotTime: transcriptSnapshot.snapshotTime,
        },
      });
    });
  };

  appendAudioBlob = async (noteId: string, blobData: Blob) => {
    // Use a transaction to prevent race conditions
    await this.table.db.transaction("rw", this.table, async () => {
      // Get the next blob index within the transaction
      const currentChunkCount = await this.table.where({ noteId }).count();

      // Add the new chunk - the transaction ensures atomicity
      await this.table.add({
        noteId,
        blobIndex: currentChunkCount,
        blobData,
      });
    });
  };

  getAudioCaptureState = async () => {
    const records = orderBy(await this.table.toArray(), "blobIndex", "desc");
    const lastRecord = records[0];
    if (!lastRecord) return;

    const noteId = lastRecord.noteId;
    const data = records.filter(d => d.noteId === noteId);

    const capturedAudioId = records.find(d => "capturedAudioId" in d)?.capturedAudioId;

    if (!capturedAudioId) {
      logger.error({
        message: "[AudioCapturesDB] getAudioCaptureState",
        info: objectModule.safeAsJson({
          error: "No captured audio id found",
          noteId,
          records,
        }),
      });
      return;
    }

    const transcriptSnapshots = orderBy(
      records.filter(d => "transcriptSnapshot" in d).map(d => d.transcriptSnapshot),
      "lastAudioEnd"
    ).map(d => {
      const map = new Map<number, AssemblyAIMessage>();
      for (const transcript of d.transcripts) {
        map.set(transcript.audio_start, transcript);
      }
      return {
        transcriptMap: map,
        lastAudioEnd: d.lastAudioEnd,
        snapshotTime: d.snapshotTime,
      } as TranscriptSnapshot;
    });

    const transcripts = sortBy(
      records.filter(d => "transcript" in d),
      "audio_start"
    ).map(d => d.transcript);

    const audioBlobs = sortBy(
      data.filter(d => "blobData" in d),
      "blobIndex"
    ).map(d => d.blobData);

    return { transcriptSnapshots, transcripts, audioBlobs, noteId, capturedAudioId };
  };
}
